import { Button, Component, EditBox, Label, Node, Slider, _decorator, log, Prefab, NodePool, instantiate, Color, Sprite, ScrollView } from 'cc';
import Game from '../../../Game';
import MultiThemeButton from '../../Components/MultiTheme/MultiThemeButton';
import AudioClipItem from './AudioClipItem';
import { unwatch, watch } from '../../Utils/Watch';
import { VOLUME_BASE } from './AudioInspector';

const { ccclass, property } = _decorator;

@ccclass('AudioClipItemView')
export default class AudioClipItemView extends Component {
    @property(EditBox)
    public readonly audioLimitNum: EditBox = null;
    @property(Label)
    private readonly audioName: Label = null;
    @property(Label)
    private readonly audioCount: Label = null;
    @property(MultiThemeButton)
    private readonly volumeSwitch: MultiThemeButton = null;
    @property(Slider)
    private readonly volumeSlider: Slider = null;
    @property(EditBox)
    private readonly volumeEditBox: EditBox = null;
    @property(Node)
    private readonly disableMask: Node = null;
    @property(Button)
    private readonly logAudioSourceButton: Button = null;
    @property(Node)
    private readonly volumeBarRoot: Node = null;
    @property(Prefab)
    private readonly block: Prefab = null;

    public currentItem: AudioClipItem = null;
    protected blockPool: NodePool = new NodePool();
    private _currentWatchingData: AudioClipItem = null;
    private _scrollView: ScrollView = null;
    private _onVolumeRateChangedCallback: () => void = this.onVolumeRateChanged.bind(this);

    public get volume() {
        return this.currentItem ? this.currentItem.volume : 0;
    }

    public get isEnabled() {
        return !this.disableMask.active;
    }

    public get audioSourceCount() {
        return Object.keys(this.currentItem.audioSource).length;
    }

    protected get volumeGain() {
        const audioSourceName = Object.values(this.currentItem.audioSource)[0]?.clip.name;
        // @ts-expect-error
        return Game.audio._getAudioClipState(audioSourceName)?.volumeGain;
    }

    public updateView(itemData: AudioClipItem) {
        const audioSources = Object.values(this.currentItem?.audioSource ?? []);
        if (audioSources.length > 0) {
            unwatch(audioSources[0], '_volumeRate', this._onVolumeRateChangedCallback);
        }
        if (!itemData) {
            return;
        }
        this.currentItem = itemData;
        this.audioName.string = this.currentItem.audioName;
        this.audioLimitNum.string = this.currentItem.audioLimitNum.toString();
        this.updateAudioCount();
        this.updateVolumeDisplay(this.volume);
        const newAudioSources = Object.values(this.currentItem?.audioSource ?? []);
        if (newAudioSources.length > 0) {
            this.onVolumeRateChanged();
            watch(newAudioSources[0], '_volumeRate', this._onVolumeRateChangedCallback);
        }
    }

    public resetVolume() {
        this.updateVolumeDisplay(100);
    }

    public updateAudioCount() {
        const audioCount = this.audioSourceCount;
        this.audioCount.string = audioCount.toString();
        this.updateStatus(audioCount > 0);
    }

    public setVolume() {
        const audioSourceName = Object.values(this.currentItem.audioSource)[0]?.usedClip.name;
        // @ts-expect-error
        const clipState = Game.audio._getAudioClipState(audioSourceName);
        // @ts-expect-error
        clipState.volumeGain = (this.currentItem.isMuted ? 0 : this.volume) / VOLUME_BASE;

        // call _syncAudioSourceVolume throught setVolumeOf
        Object.values(this.currentItem.audioSource).forEach((audioSource) => {
            Game.audio.setVolumeOf(audioSource, audioSource.volumeRate);
        });

        // Object.values(this.currentItem.audioSource).forEach((audioSource, index) => {
        //     if (index > this.currentItem.audioLimitNum - 1) {
        //         Game.audio.setVolumeOf(audioSource, 0);
        //     } else {
        //         Game.audio.setVolumeOf(audioSource, (this.currentItem.isMuted ? 0 : this.volume) / VOLUME_BASE);
        //     }
        // });
    }

    protected onLoad() {
        this.volumeSwitch.on(Button.EventType.CLICK, this.updateVolumeDisplayBySwitch, this);
        this.volumeSlider.on(Slider.EventType.SLIDE, this.updateVolumeDisplayBySlider, this);
        this.volumeSlider.on(Slider.EventType.SLIDE, this.updateVolumeDisplayBySlider, this);
        this.volumeEditBox.on(EditBox.EventType.EDITING_DID_ENDED, this.recoverVolumeOnEditBox, this);
        this.volumeEditBox.on(EditBox.EventType.EDITING_RETURN, this.updateVolumeDisplayByEditBox, this);
        this.audioLimitNum.on(EditBox.EventType.EDITING_DID_ENDED, this.updateAudioLimit, this);
        this.logAudioSourceButton.on(Button.EventType.CLICK, this.logAudioSource, this);

        this._scrollView = this.node.getComponentInParent(ScrollView);
        if (this._scrollView) {
            this.volumeSlider.node.on(Node.EventType.TOUCH_START, () => {
                this._scrollView.enabled = false;
            });
            this.volumeSlider.node.on(Node.EventType.TOUCH_END, () => {
                this._scrollView.enabled = true;
            });
            this.volumeSlider.node.on(Node.EventType.TOUCH_CANCEL, () => {
                this._scrollView.enabled = true;
            });
            this.volumeSlider.handle.node.on(Node.EventType.TOUCH_START, () => {
                this._scrollView.enabled = false;
            });
            this.volumeSlider.handle.node.on(Node.EventType.TOUCH_END, () => {
                this._scrollView.enabled = true;
            });
            this.volumeSlider.handle.node.on(Node.EventType.TOUCH_CANCEL, () => {
                this._scrollView.enabled = true;
            });
        }
    }

    protected onVolumeRateChanged() {
        // 格子最大長度
        const blockLimitNum = 17;

        const audioSources = Object.values(this.currentItem?.audioSource ?? []);
        const targetBlcokNum = audioSources.length > 0 ? Math.floor((audioSources[0]._volumeRate * blockLimitNum) / 3) : 0;
        // ? Object.values(this.currentItem.audioSource).reduce((acc, audioSource) => acc + audioSource.volumeRate * 5, 0)

        const currentBlockList = this.volumeBarRoot.children;
        if (currentBlockList.length === targetBlcokNum) {
            return;
        }

        let color: Color;
        if (targetBlcokNum > (blockLimitNum * 5) / 6) {
            color = Color.RED;
        } else if (targetBlcokNum > blockLimitNum / 2) {
            color = Color.YELLOW;
        } else {
            color = Color.GREEN;
        }

        const diff = targetBlcokNum - currentBlockList.length;

        if (diff > 0) {
            for (let i = 0; i < targetBlcokNum; i++) {
                if (i === blockLimitNum) {
                    break;
                }
                if (i < targetBlcokNum - diff) {
                    currentBlockList[i].getComponent(Sprite).color = color;
                } else {
                    let block = this.blockPool.get();
                    if (!block) {
                        block = instantiate(this.block);
                    }
                    block.getComponent(Sprite).color = color;
                    block.setParent(this.volumeBarRoot);
                }
            }
        } else {
            for (let i = 0; i < currentBlockList.length; i++) {
                if (i === blockLimitNum) {
                    break;
                }
                if (i < targetBlcokNum) {
                    currentBlockList[i].getComponent(Sprite).color = color;
                } else {
                    const removeNodes = currentBlockList.slice(i, i - diff);
                    removeNodes.forEach((node) => {
                        this.blockPool.put(node);
                    });
                    break;
                }
            }
        }
    }

    protected logAudioSource() {
        Object.values(this.currentItem.audioSource).forEach((audioSource) => {
            // @ts-expect-error
            log('AudioSource: ', audioSource, '\nAudioClipState: ', Game.audio._getAudioClipState(audioSource.clip.name));
        });
    }

    protected recoverVolumeOnEditBox() {
        this.volumeEditBox.string = `${this.volume.toFixed(0)}%`;
    }

    protected updateStatus(enableItem: boolean) {
        this.disableMask.active = !enableItem;
    }

    protected updateVolumeDisplay(volume: number) {
        this.currentItem.volume = volume;
        this.volumeEditBox.string = `${this.volume.toFixed(0)}%`;
        this.volumeSlider.progress = this.volume / this.currentItem.maximumVolume;

        if (this.currentItem.isMuted || this.volume === 0) {
            this.volumeSwitch.theme = 0;
        } else if (this.volume < this.currentItem.maximumVolume * 0.22) {
            this.volumeSwitch.theme = 1;
        } else if (this.volume < this.currentItem.maximumVolume * 0.5) {
            this.volumeSwitch.theme = 2;
        } else {
            this.volumeSwitch.theme = 3;
        }

        this.setVolume();
    }

    protected updateVolumeDisplayBySlider() {
        this.currentItem.isMuted = false;
        this.updateVolumeDisplay(this.volumeSlider.progress * this.currentItem.maximumVolume);
    }

    protected updateVolumeDisplayByEditBox() {
        const volume: number = +this.volumeEditBox.string.split('%')[0];
        this.currentItem.maximumVolume = volume;
        this.currentItem.isMuted = false;
        this.updateVolumeDisplay(volume * this.volumeSlider.progress);
    }

    protected updateVolumeDisplayBySwitch() {
        this.currentItem.isMuted = !this.currentItem.isMuted;
        this.updateVolumeDisplay(this.volume);
    }

    protected updateAudioLimit() {
        this.currentItem.audioLimitNum = +parseInt(this.audioLimitNum.string.slice(0, 2), 10).toFixed(0);
        this.audioLimitNum.string = this.currentItem.audioLimitNum.toString();
        this.setVolume();
    }
}
